import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { ROLE_DESCRIPTION_MAX, ROLE_KEY_PATTERN, ROLE_NAME_MAX } from '@rentflowdev/common/roles'
import { AlertCircleIcon } from 'lucide-react'
import {
	Alert, AlertDescription, AlertTitle,
	Checkbox,
	Form, FormControl, FormDescription, FormDialog, FormField, FormItem, FormLabel, FormMessage,
	Input,
	Textarea,
} from '@rentflowdev/ui'
import { useCreateRoleMutation, useGetRoleVocabularyQuery, useUpdateRoleMutation } from '@/api'
import { organizationSegmentLabel } from '@/lib/account-labels'
import { extractErrorMessage } from '@/lib/error-message'
import { zodResolverI18n } from '@/lib/zod-resolver-i18n'
import { ORGANIZATION_SEGMENTS, type RoleDefinition } from '@/types'
import { GrantsMatrix } from './grants-matrix'

/**
 * Mirrors Prisma `Role` with its `RoleGrant` rows (ADR 0003).
 * DB fields not rendered:
 * - system (only the seeded owner role has it, and it is never edited here)
 * - position (a new role goes last; an edited one keeps its place)
 */
const roleSchema = z.object({
	// The same limits the gateway and auth-service apply.
	key:         z.string().trim().regex(ROLE_KEY_PATTERN, 'admin.roles.form.keyInvalid'),
	name:        z.string().trim().min(1).max(ROLE_NAME_MAX),
	description: z.string().trim().max(ROLE_DESCRIPTION_MAX).optional(),
	segments:    z.array(z.enum(ORGANIZATION_SEGMENTS)),
	grants:      z.array(z.object({
		resource: z.string(),
		action:   z.string(),
		scopes:   z.array(z.string()).min(1),
	})),
})

type RoleFormValues = z.infer<typeof roleSchema>

const EMPTY: RoleFormValues = { key: '', name: '', description: '', segments: [], grants: [] }

function valuesOf(role: RoleDefinition | null): RoleFormValues {
	if (!role) return EMPTY
	return {
		key:         role.key,
		name:        role.name,
		description: role.description ?? '',
		segments:    role.segments,
		grants:      role.grants,
	}
}

interface RoleDialogProps {
	open:         boolean
	onOpenChange: (open: boolean) => void
	/** The role to edit; null to add one. */
	role:         RoleDefinition | null
	/** Where a new role goes in the lists. */
	nextPosition: number
}

export function RoleDialog({ open, onOpenChange, role, nextPosition }: RoleDialogProps) {
	const { t } = useTranslation()
	const [createRole, { isLoading: isCreating }] = useCreateRoleMutation()
	const [updateRole, { isLoading: isUpdating }] = useUpdateRoleMutation()
	const { data: vocabulary } = useGetRoleVocabularyQuery(undefined, { skip: !open })
	const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)
	// The owner role is shown, not edited: auth-service refuses changes to it.
	const readOnly = role?.system ?? false

	const form = useForm<RoleFormValues>({
		resolver:      zodResolverI18n<RoleFormValues>(roleSchema),
		defaultValues: valuesOf(role),
		mode:          'onBlur',
	})

	useEffect(() => {
		if (open) form.reset(valuesOf(role))
	}, [open, role, form])

	// A closed dialog forgets its error, so the next one opens clean.
	function handleOpenChange(next: boolean) {
		if (!next) setErrorMsg(undefined)
		onOpenChange(next)
	}

	async function onSubmit(values: RoleFormValues) {
		setErrorMsg(undefined)
		const body = {
			name:        values.name,
			description: values.description || undefined,
			segments:    values.segments,
			grants:      values.grants,
		}
		try {
			if (role) await updateRole({ key: role.key, body: { ...body, position: role.position } }).unwrap()
			else await createRole({ ...body, key: values.key, position: nextPosition }).unwrap()
			handleOpenChange(false)
		} catch (err) {
			setErrorMsg(extractErrorMessage(err))
		}
	}

	return (
		<Form {...form}>
			<FormDialog
				open={open}
				onOpenChange={handleOpenChange}
				title={role ? t('admin.roles.form.editTitle', { name: role.name }) : t('admin.roles.form.createTitle')}
				onSubmit={form.handleSubmit(onSubmit)}
				isSubmitting={isCreating || isUpdating}
				isSubmitDisabled={readOnly}
				submitLabel={role ? t('admin.roles.form.submitSave') : t('admin.roles.form.submitCreate')}
				cancelLabel={readOnly ? t('admin.roles.form.close') : t('common.cancel')}
				contentClassName="sm:max-w-4xl max-h-[90vh] overflow-y-auto"
			>
				<div className="flex flex-col gap-4">
					{errorMsg !== undefined && (
						<Alert variant="destructive">
							<AlertCircleIcon className="size-4" />
							<AlertTitle>{t('admin.roles.errors.save')}</AlertTitle>
							{errorMsg && <AlertDescription>{errorMsg}</AlertDescription>}
						</Alert>
					)}

					<div className="grid gap-4 sm:grid-cols-2">
						<FormField
							control={form.control}
							name="key"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('admin.roles.form.key')}</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder="LEASING_AGENT"
											className="font-mono"
											disabled={role !== null}
											onChange={(e) => field.onChange(e.target.value.toUpperCase())}
										/>
									</FormControl>
									<FormDescription>{t('admin.roles.form.keyHint')}</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>{t('admin.roles.form.name')}</FormLabel>
									<FormControl>
										<Input {...field} disabled={readOnly} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<FormField
						control={form.control}
						name="description"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('admin.roles.form.descriptionLabel')}</FormLabel>
								<FormControl>
									<Textarea
										{...field}
										rows={2}
										placeholder={t('admin.roles.form.descriptionPlaceholder')}
										disabled={readOnly}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="segments"
						render={() => (
							<FormItem>
								<FormLabel>{t('admin.roles.form.segments')}</FormLabel>
								<div className="flex flex-wrap gap-4">
									{ORGANIZATION_SEGMENTS.map((segment) => (
										<FormField
											key={segment}
											control={form.control}
											name="segments"
											render={({ field }) => (
												<FormItem className="flex flex-row items-center gap-2 space-y-0">
													<FormControl>
														<Checkbox
															checked={field.value?.includes(segment)}
															disabled={readOnly}
															onCheckedChange={(checked) => {
																const current = field.value ?? []
																return checked
																	? field.onChange([...current, segment])
																	: field.onChange(current.filter((v) => v !== segment))
															}}
														/>
													</FormControl>
													<FormLabel className="font-[500]">{organizationSegmentLabel(t, segment)}</FormLabel>
												</FormItem>
											)}
										/>
									))}
								</div>
								<FormDescription>{t('admin.roles.form.segmentsHint')}</FormDescription>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="grants"
						render={({ field }) => (
							<FormItem>
								<FormLabel>{t('admin.roles.form.grants')}</FormLabel>
								<FormDescription>{t('admin.roles.form.grantsHint')}</FormDescription>
								<FormControl>
									<GrantsMatrix
										value={field.value}
										onChange={field.onChange}
										vocabulary={vocabulary ?? []}
										disabled={readOnly}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>
			</FormDialog>
		</Form>
	)
}
